/*
 * Proton PRE-ORDER storefront layer — 2026-08-07
 *
 * Built for Project Coyote, but generic: add a handle to HANDLES and it works.
 *
 * WHY THIS EXISTS
 *   A Shopify "pre-order" is really just `inventoryPolicy: CONTINUE` + 0 on hand.
 *   Natively the storefront tells the shopper NOTHING — the button still says
 *   "Add to cart", the card looks in-stock, and the cart line is indistinguishable
 *   from a paddle sitting in the warehouse. Diego's requirement was the opposite:
 *   "preorder only so that they know it's not actually there." This is that layer.
 *
 * WHAT IT DOES
 *   1. PDP  — button becomes PRE-ORDER NOW, a notice card is injected under the
 *             buy buttons, and a hidden line-item property is added to the form so
 *             every pre-order order line is stamped (shows on the order + pick list).
 *   2. GRID — a PRE-ORDER pill on any product card linking to a pre-order handle.
 *   3. CART — a PRE-ORDER pill on the line + a short notice.
 *   4. Suppresses the RESET cleaner upsell on a pre-order PDP (it is a Shopify
 *      Collective dropship item; pairing it with a pre-order guarantees a split
 *      shipment and the "SHIPS SEPARATELY" pick-list blindspot).
 *
 * SAFE BY CONSTRUCTION: everything is scoped to HANDLES. On every other page in
 * the store this script observes nothing and changes nothing.
 *
 * Deploy: node ~/proton/scripts/_deploy-proton-preorder.js
 * Remove: node ~/proton/scripts/_deploy-proton-preorder.js --remove
 */
(function () {
  "use strict";

  var HANDLES = ["project-coyote-preorder"];

  // Estimated ship window shown on the PDP notice. Keep it in sync with the
  // product description — ~/proton-coyote-launch/PREORDER/set_ship_window.js
  // rewrites BOTH in one command. null => the notice omits the date line.
  var SHIP_TEXT = "Ships within 2 weeks";

  // The long-form build story lives on its own page. Without a hook from the
  // PDP nothing on the storefront links to it, so it only ever gets traffic
  // from ads. Set to null to pull the link without touching anything else.
  var LANDING_URL = "/pages/coyote-preorder";
  var LANDING_LABEL = "See how it's built";

  var LABEL = "PRE-ORDER NOW";
  var PILL = "PRE-ORDER";
  var PROP_NAME = "Pre-order";
  // ⚠️ This value is PRINTED IN THE CART, on the order and on the packing slip —
  // it is customer-facing copy, not an internal note, so it obeys the same standing
  // spec as the cart note: positive pre-order framing, never "not in stock".
  // Nothing keys on this string (fulfilment routes off the `preorder` product TAG);
  // the property NAME is the stable identifier.
  var PROP_VALUE = "Reserved from the first production run";

  var ORANGE = "#FF6000";
  var CREAM = "#DECAA4";
  var CHARCOAL = "#0B0B0D";

  function log() {
    try { console.info.apply(console, ["[proton-preorder]"].concat([].slice.call(arguments))); } catch (e) {}
  }

  function isPreorderHref(href) {
    if (!href) return false;
    for (var i = 0; i < HANDLES.length; i++) {
      if (href.indexOf("/products/" + HANDLES[i]) !== -1) return true;
    }
    return false;
  }

  // ⚠️ Do NOT detect the PDP from location.pathname alone. Shopify's draft-product
  // preview serves the real product template at /products_preview?preview_key=…,
  // so a path test reports "not a PDP" on exactly the URL you verify with.
  // ShopifyAnalytics.meta.product.handle is printed natively on every product page
  // and is correct in both cases.
  function currentProductHandle() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && m.product.handle) return m.product.handle;
    } catch (e) {}
    try {
      var c = document.querySelector('link[rel="canonical"]');
      if (c && c.href) {
        var mm = c.href.match(/\/products\/([^/?#]+)/);
        if (mm) return mm[1];
      }
    } catch (e) {}
    var mp = (location.pathname || "").match(/\/products\/([^/?#]+)/);
    return mp ? mp[1] : null;
  }

  function onPreorderPDP() {
    var h = currentProductHandle();
    if (!h) return false;
    for (var i = 0; i < HANDLES.length; i++) if (HANDLES[i] === h) return true;
    return false;
  }

  // ---------------------------------------------------------------- styles
  function injectCSS() {
    if (document.getElementById("proton-preorder-css")) return;
    var s = document.createElement("style");
    s.id = "proton-preorder-css";
    s.textContent = [
      ".pp-pill{display:inline-block;font:700 11px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;",
      "letter-spacing:.16em;text-transform:uppercase;color:#fff;background:" + ORANGE + ";",
      "padding:6px 10px;border-radius:999px;white-space:nowrap;vertical-align:middle}",
      ".pp-card-pill{position:absolute;top:10px;left:10px;z-index:3;pointer-events:none}",
      ".pp-note{border:1px solid " + ORANGE + ";background:" + CHARCOAL + ";border-radius:12px;",
      "padding:14px 16px;margin:14px 0 4px;color:" + CREAM + ";",
      "font:400 14px/1.55 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}",
      ".pp-note b{color:#fff}",
      ".pp-note .pp-h{display:block;color:" + ORANGE + ";font-weight:700;font-size:11px;",
      "letter-spacing:.16em;text-transform:uppercase;margin-bottom:6px}",
      ".pp-cart-note{margin-top:6px;color:#8a8175;font-size:12px;line-height:1.4}",
      // ⚠️ the PDP is a LIGHT theme. A tinted-orange panel with cream type is
      // invisible on white — this has to carry its own dark ground, the same
      // way .pp-note does, or the link washes out completely.
      ".pp-learn{display:flex;align-items:center;justify-content:space-between;gap:12px;",
      "margin:12px 0 4px;padding:13px 16px;border-radius:12px;text-decoration:none;",
      "border:1px solid " + ORANGE + ";background:" + CHARCOAL + ";color:" + CREAM + ";",
      "font:700 14px/1.3 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;",
      "box-shadow:inset 0 0 40px rgba(255,96,0,.16);transition:box-shadow .2s}",
      ".pp-learn:hover{box-shadow:inset 0 0 60px rgba(255,96,0,.3)}",
      ".pp-learn small{display:block;margin-top:4px;font-weight:400;font-size:12.5px;",
      "color:rgba(255,255,255,.62)}",
      ".pp-learn i{font-style:normal;color:" + ORANGE + ";font-size:20px;line-height:1}"
    ].join("");
    document.head.appendChild(s);
  }

  // ---------------------------------------------------------------- PDP
  function decoratePDP() {
    // Every add-to-cart button on the page, including sticky/quick bars.
    var btns = document.querySelectorAll('form[action*="/cart/add"] button[name="add"], form[action*="/cart/add"] [type="submit"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.getAttribute("data-pp") === "1") continue;
      var span = b.querySelector("span:not(.visually-hidden):not(.loading-overlay__spinner)") || b;
      // Never overwrite Shopify's own sold-out / unavailable state.
      var txt = (span.textContent || "").trim().toLowerCase();
      if (txt.indexOf("sold out") !== -1 || txt.indexOf("unavailable") !== -1) continue;
      span.textContent = LABEL;
      b.setAttribute("data-pp", "1");
    }

    // Stamp the order line so fulfilment can see it without opening the product.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var j = 0; j < forms.length; j++) {
      var f = forms[j];
      if (f.querySelector('input[data-pp-prop="1"]')) continue;
      var inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = "properties[" + PROP_NAME + "]";
      inp.value = PROP_VALUE;
      inp.setAttribute("data-pp-prop", "1");
      f.appendChild(inp);
    }

    // The notice, directly under the buy buttons — but only if the product
    // description isn't already carrying one. The description block is the
    // server-rendered source of truth (it also travels to Shop / Google / email);
    // this JS note is the fallback for themes that bury the description in a tab.
    // ⚠️ Do NOT test on a class alone: the storefront can serve a cached copy of
    // an older description for up to ~60 min after a write, so the class may be
    // missing on a product that genuinely has the block. Test the text.
    var descHasNotice = false;
    try {
      var desc = document.querySelector(".product__description, .product-single__description, [class*='product__description']");
      if (desc && /not in stock/i.test(desc.textContent || "")) descHasNotice = true;
    } catch (e) {}

    if (!document.querySelector(".pp-note") && !descHasNotice) {
      var host = document.querySelector(".product-form__buttons") ||
                 document.querySelector('form[action*="/cart/add"]');
      if (host) {
        var d = document.createElement("div");
        d.className = "pp-note";
        d.innerHTML = '<span class="pp-h">Pre-order · not in stock yet</span>' +
          "This paddle has not shipped yet. You are reserving one from the <b>first production run</b>. " +
          "Pre-orders are filled in the order they were placed." +
          (SHIP_TEXT ? " Estimated shipping: <b>" + SHIP_TEXT + "</b>." : "");
        host.parentNode.insertBefore(d, host.nextSibling);
      }
    }

    // Link out to the long-form build page, directly under the buy buttons.
    // If the JS notice above was the one that rendered it is a sibling of the
    // buy block, so the link goes after IT and the order reads buy -> terms ->
    // learn more. When the server-rendered .pp-note is used instead it lives
    // inside the product description, far down the DOM — `contains` is false,
    // and the link correctly stays anchored to the buy buttons rather than
    // being flung into the middle of the spec list.
    if (LANDING_URL && !document.querySelector(".pp-learn")) {
      var buy = document.querySelector(".product-form__buttons") ||
                document.querySelector('form[action*="/cart/add"]');
      if (buy) {
        var note = document.querySelector(".pp-note");
        var anchorEl = (note && buy.parentNode.contains(note)) ? note : buy;
        var a = document.createElement("a");
        a.className = "pp-learn";
        a.href = LANDING_URL;
        a.innerHTML = "<span>" + LANDING_LABEL +
          "<small>The hybrid core, the throat taper, the colourway.</small></span><i>&rsaquo;</i>";
        anchorEl.parentNode.insertBefore(a, anchorEl.nextSibling);
      }
    }
  }

  // ---------------------------------------------------------------- grid cards
  function decorateCards() {
    // On the pre-order product's own page every "view details" link matches, and
    // a loose ancestor lookup lands the pill on top of the product title. The PDP
    // has its own treatment — cards are a grid-only concern.
    if (onPreorderPDP()) return;
    var links = document.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (!isPreorderHref(a.getAttribute("href"))) continue;
      // Require Dawn's real card structure. No .grid__item fallback: that matches
      // the main product section on a PDP.
      var card = a.closest(".card-wrapper") || a.closest(".card");
      if (!card || card.querySelector(".pp-card-pill")) continue;
      var media = card.querySelector(".card__media");
      if (!media) continue;
      // ⚠️ never put filter/transform on .card-wrapper — it breaks the quick-add
      // modal (see memory: storefront-soldout-dim). position:relative is safe.
      var cs = window.getComputedStyle(media);
      if (cs.position === "static") media.style.position = "relative";
      var pill = document.createElement("span");
      pill.className = "pp-pill pp-card-pill";
      pill.textContent = PILL;
      media.appendChild(pill);
    }
  }

  // ---------------------------------------------------------------- cart
  function decorateCart() {
    var rows = document.querySelectorAll(".cart-item, .cart-drawer .cart-item, tr.cart-item");
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (row.querySelector(".pp-pill")) continue;
      var a = row.querySelector('a[href*="/products/"]');
      if (!a || !isPreorderHref(a.getAttribute("href"))) continue;
      var name = row.querySelector(".cart-item__name") || a;
      var wrap = document.createElement("div");
      wrap.innerHTML = '<span class="pp-pill">' + PILL + "</span>" +
        // ⚠️ STANDING COPY SPEC: this line is the LAST thing a shopper reads before
        // checkout. "NOT IN STOCK" / "NOT IN STOCK YET" is forbidden storefront-wide —
        // the approved framing is positive pre-order language. Never print a calendar
        // date here; the only permitted timing phrase is the 2-3 week window in
        // SHIP_TEXT above.
        '<div class="pp-cart-note">Pre-order — reserved for you from the first production run' +
        (SHIP_TEXT ? " (" + SHIP_TEXT + ")" : "") + ".</div>";
      name.parentNode.insertBefore(wrap, name.nextSibling);
    }
  }

  // ---------------------------------------------------------------- boot
  function run() {
    injectCSS();
    if (onPreorderPDP()) {
      // The RESET cleaner is a dropship accessory. Offering it on a pre-order
      // page manufactures a split shipment; stand it down here only.
      window.PROTON_CLEANER_ENABLED = false;
      decoratePDP();
    }
    decorateCards();
    decorateCart();
  }

  function boot() {
    run();
    try {
      var mo = new MutationObserver(function () {
        if (mo._t) return;
        mo._t = setTimeout(function () { mo._t = null; run(); }, 120);
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    document.addEventListener("cart:refresh", run);
    document.addEventListener("cart:updated", run);
    log("booted", HANDLES.join(","), "pdp=" + onPreorderPDP());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.ProtonPreorder = { run: run, handles: HANDLES, _booted: true };
})();
